import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ShieldCheckIcon, LockIcon, StarIcon, ThumbsUpIcon, ChevronDownIcon, ChevronUpIcon, CheckCircle2Icon } from 'lucide-react';
import { getLocalizedPlans } from '../utils/pricing';
interface CheckoutScreenProps {
  petName: string;
}
const BENEFITS = ['Mejora la obediencia rápidamente', 'Un perro más tranquilo en situaciones cotidianas', 'Confianza para llevar a tu perro a cualquier lugar', 'Comodidad alrededor de invitados y otros perros', 'Una vida más fácil con tu perro'];
const FAQS = [{
  q: 'He probado otros programas antes. ¿En qué se diferencia esto?',
  a: 'KALMIA no es el tradicional entrenamiento canino. Es una rutina de modales para perros pequeños diseñada alrededor de cómo los perros pequeños realmente viven y se comportan. Usamos un enfoque suave, respaldado por la ciencia, enfocado en los modales diarios — no en comandos, presión o control.'
}, {
  q: 'Mi perro no está motivado por la comida',
  a: '¡No hay problema! Nuestra rutina te enseña cómo usar el afecto, el juego y el entorno como recompensas, para que no tengas que depender solo de premios.'
}, {
  q: '¿Cuánto tiempo toma ver resultados?',
  a: 'Muchos dueños notan una diferencia en la calma y atención de su perro en los primeros días. Siguiendo la rutina consistentemente, verás mejoras significativas en semanas.'
}];
const TESTIMONIALS = [{
  name: 'Krsix',
  title: 'Los resultados fueron rápidos',
  text: 'He visto mejoras inmediatas, amigos y familiares han notado verbalmente la diferencia en el comportamiento y los modales de nuestro perro.',
  stars: 5
}, {
  name: 'Pkbyron',
  title: 'KALMIA ofrece consejos personalizados.',
  text: 'KALMIA ofrece consejos prácticos para enseñarle modales a tu perro. Se enfoca en tus áreas problemáticas como tirar de la correa y ladrar según tus necesidades. Es un gran recurso y valor.',
  stars: 5
}, {
  name: 'Christina L.',
  title: 'Apenas he empezado y tenía mis dudas...',
  text: 'Apenas he empezado y tenía mis dudas, pero los consejos que estoy aprendiendo están haciendo que mi perro sin modales sea más fácil de manejar.',
  stars: 5
}];
function useCountdown(startSeconds: number) {
  const [seconds, setSeconds] = useState(startSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSeconds(prev => {
        if (prev <= 0) return startSeconds;
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startSeconds]);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}
export function CheckoutScreen({
  petName
}: CheckoutScreenProps) {
  const plans = useMemo(() => getLocalizedPlans(), []);
  const [selectedPlan, setSelectedPlan] = useState('24weeks');
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const name = petName || 'Coco';
  const timer = useCountdown(599);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const handleScrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const handlePurchase = () => {
    const plan = plans.find(p => p.id === selectedPlan);
    if (plan) {
      if ((window as any).fbq) {
        ;
        (window as any).fbq('track', 'Purchase', {
          value: plan.price,
          currency: plan.currency,
          content_name: plan.label,
          content_ids: [plan.id]
        });
      }
      window.open(plan.url, '_blank');
    }
  };
  return <div className="flex flex-col w-full min-h-full relative bg-white" data-id="element-237">
      {/* STICKY TOP BAR */}
      <div className="sticky top-0 z-50 bg-white border-b border-stone-100 shadow-sm" data-id="element-238">
        <div className="flex items-center justify-between px-4 py-3" data-id="element-239">
          <div className="flex items-center gap-2" data-id="element-240">
            <div className="bg-blue-50 text-blue-600 font-bold text-xl px-2 py-0.5 rounded" data-id="element-241">
              {timer}
            </div>
            <div className="flex flex-col" data-id="element-242">
              <span className="text-[10px] font-bold text-stone-400 uppercase leading-none" data-id="element-243">
                minutos
              </span>
              <span className="text-[10px] font-bold text-stone-400 uppercase leading-none mt-0.5" data-id="element-244">
                segundos
              </span>
            </div>
          </div>
          <button onClick={handleScrollToCta} className="bg-orange-500 hover:bg-orange-600 text-white text-[13px] font-bold px-4 py-2 rounded-md transition-colors shadow-sm uppercase tracking-wide" data-id="element-245">
            Obtener Mi Rutina
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pt-[0px] pb-[0px]" data-id="element-246">
        {/* HEADER SECTION */}
        <div className="px-5 pt-8 pb-6 text-center" data-id="element-247">
          <h1 className="text-[28px] font-extrabold text-stone-900 leading-tight mb-6" data-id="element-248">
            Tu perro puede cambiar más rápido de lo que crees
          </h1>

          <div className="flex justify-center gap-6 mb-2" data-id="element-249">
            <div className="flex flex-col items-center text-center" data-id="element-250">
              <div className="w-12 h-12 flex items-center justify-center mb-2" data-id="element-251">
                <span className="text-3xl" data-id="element-252">🎯</span>
              </div>
              <span className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold" data-id="element-253">
                Objetivo
              </span>
              <span className="text-[14px] font-bold text-stone-800" data-id="element-254">
                Tener un perro tranquilo y fácil de manejar
              </span>
            </div>
            <div className="w-px bg-stone-200" data-id="element-255" />
            <div className="flex flex-col items-center text-center" data-id="element-256">
              <div className="w-12 h-12 flex items-center justify-center mb-2" data-id="element-257">
                <span className="text-3xl" data-id="element-258">⚠️</span>
              </div>
              <span className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold" data-id="element-259">
                Problemas
              </span>
              <span className="font-bold text-stone-800 text-[14px]" data-id="element-260">
                Reacciones sin control en momentos clave
              </span>
            </div>
          </div>
        </div>

        {/* PRICING SECTION */}
        <div className="bg-blue-50/50 py-8 px-5" data-id="element-261">
          <div className="flex flex-col gap-4 mb-4" data-id="element-262">
            {plans.map(plan => {
            const isSelected = selectedPlan === plan.id;
            const isPopular = plan.popular;
            // Determine accent colors based on rules
            const borderColor = isSelected ? isPopular ? 'border-blue-500' : 'border-[#ff5a5f]' : 'border-stone-200';
            const radioColor = isSelected ? isPopular ? 'bg-blue-500' : 'bg-[#ff5a5f]' : 'bg-transparent';
            const radioBorderColor = isSelected ? isPopular ? 'border-blue-500' : 'border-[#ff5a5f]' : 'border-stone-300';
            return <motion.div key={plan.id} onClick={() => setSelectedPlan(plan.id)} whileTap={{
              scale: 0.98
            }} className={`relative bg-white rounded-2xl border-2 cursor-pointer transition-all duration-200 ${borderColor} shadow-sm`} data-id="element-263">
                  {isPopular && <div className="bg-[#dbeafe] text-[#3b82f6] text-[11px] font-bold py-1.5 text-center flex items-center justify-center gap-1.5 rounded-t-xl uppercase tracking-wider" data-id="element-264">
                      <ThumbsUpIcon size={14} className="fill-current" data-id="element-265" /> MÁS
                      POPULAR
                    </div>}

                  <div className={`p-5 flex items-center justify-between ${isPopular ? 'pt-4' : ''}`} data-id="element-266">
                    <div className="flex items-center gap-4" data-id="element-267">
                      {/* Radio Button */}
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${radioBorderColor}`} data-id="element-268">
                        {isSelected && <div className={`w-3 h-3 rounded-full ${radioColor}`} data-id="element-269" />}
                      </div>

                      {/* Plan Info */}
                      <div data-id="element-270">
                        <h3 className="text-[15px] font-extrabold text-stone-800 tracking-wide" data-id="element-271">
                          {plan.label}
                        </h3>
                        <p className="text-[13px] font-medium text-stone-400 line-through mt-0.5" data-id="element-272">
                          {plan.formattedOriginalPrice}
                        </p>
                      </div>
                    </div>

                    {/* Price Right */}
                    <div className="text-right bg-stone-100/80 px-3 py-2 rounded-lg" data-id="element-273">
                      <div className="text-[24px] font-extrabold text-stone-800 leading-none" data-id="element-274">
                        {plan.formattedPerDay}
                      </div>
                      <div className="text-[11px] font-semibold text-stone-500 mt-1" data-id="element-275">
                        por día
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* MAIN CTA */}
          <button ref={ctaRef} onClick={handlePurchase} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl text-[18px] font-bold tracking-wide transition-colors shadow-md uppercase" data-id="element-276">
            Obtener Mi Rutina
          </button>

          <div className="flex items-center justify-center gap-2 mt-4 text-stone-500" data-id="element-277">
            <ShieldCheckIcon size={18} className="text-stone-500" data-id="element-278" />
            <span className="text-[12px] font-bold tracking-wide" data-id="element-279">
              GARANTÍA DE DEVOLUCIÓN DE 30 DÍAS
            </span>
          </div>
        </div>

        {/* WHAT YOU GET SECTION */}
        <div className="px-5 py-10" data-id="element-280">
          <h2 className="text-[24px] font-extrabold text-center text-stone-900 mb-8" data-id="element-281">
            Lo Que Obtendrás
          </h2>
          <div className="flex flex-col gap-4" data-id="element-282">
            {BENEFITS.map((benefit, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-283">
                <CheckCircle2Icon className="w-6 h-6 text-blue-500 shrink-0 mt-0.5" data-id="element-284" />
                <span className="text-[16px] font-medium text-stone-700 leading-snug" data-id="element-285">
                  {benefit}
                </span>
              </div>)}
          </div>
        </div>

        {/* FAQ SECTION */}
        <div className="px-5 py-10 bg-stone-50/50" data-id="element-286">
          <h2 className="text-[24px] font-extrabold text-center text-stone-900 mb-8" data-id="element-287">
            La Gente Suele Preguntar
          </h2>
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm" data-id="element-288">
            {FAQS.map((faq, index) => <div key={index} className="border-b border-stone-100 last:border-0" data-id="element-289">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-start justify-between p-5 text-left" data-id="element-290">
                  <div className="flex gap-3" data-id="element-291">
                    <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center shrink-0 mt-0.5" data-id="element-292">
                      <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-293" />
                    </div>
                    <span className="text-[15px] font-bold text-stone-800 pr-4" data-id="element-294">
                      {faq.q}
                    </span>
                  </div>
                  <div className="shrink-0 mt-0.5" data-id="element-295">
                    {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-stone-400" data-id="element-296" /> : <ChevronDownIcon className="w-5 h-5 text-stone-400" data-id="element-297" />}
                  </div>
                </button>
                <AnimatePresence data-id="element-298">
                  {openFaq === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-299">
                      <div className="px-5 pb-5 pt-1 pl-13 text-[14px] text-stone-600 leading-relaxed" data-id="element-300">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>

        {/* TESTIMONIALS SECTION */}
        <div className="px-5 py-10" data-id="element-301">
          <h2 className="text-[24px] font-extrabold text-center text-stone-900 mb-8" data-id="element-302">
            Los Dueños de Perros Aman KALMIA
          </h2>
          <div className="flex flex-col gap-4" data-id="element-303">
            {TESTIMONIALS.map((t, i) => <div key={i} className="bg-white rounded-2xl p-5 border border-stone-200 shadow-sm" data-id="element-304">
                <h3 className="text-[16px] font-bold text-stone-800 mb-2" data-id="element-305">
                  {t.title}
                </h3>
                <div className="flex items-center justify-between mb-3" data-id="element-306">
                  <div className="flex gap-1" data-id="element-307">
                    {Array.from({
                  length: t.stars
                }).map((_, idx) => <StarIcon key={idx} className="w-4 h-4 text-orange-400 fill-orange-400" data-id="element-308" />)}
                  </div>
                  <span className="text-[12px] font-medium text-stone-400" data-id="element-309">
                    {t.name}
                  </span>
                </div>
                <p className="text-[14px] text-stone-600 leading-relaxed" data-id="element-310">
                  {t.text}
                </p>
              </div>)}
          </div>
        </div>
      </div>

      {/* STICKY BOTTOM CTA */}
      <div className="sticky bottom-0 z-50 bg-white border-t border-stone-200 p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]" data-id="element-311">
        <button onClick={() => {
        if ((window as any).fbq) {
          ;
          (window as any).fbq('track', 'InitiateCheckout');
        }
        handlePurchase();
        handleScrollToCta();
      }} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl text-[16px] font-bold tracking-wide transition-colors shadow-md uppercase" data-id="element-312">
          Obtener Mi Rutina
        </button>
      </div>
    </div>;
}